import { ReactNode, UIEvent, useMemo, useState } from 'react';

import { Box } from '@mui/material';

interface VirtualizedListProps<TItem> {
	items: TItem[];
	getItemKey: (item: TItem) => string;
	renderItem: (item: TItem) => ReactNode;
	rowHeight: number;
	maxHeight: number;
	overscan?: number;
}

const VirtualizedList = <TItem,>({
	items,
	getItemKey,
	renderItem,
	rowHeight,
	maxHeight,
	overscan = 12,
}: VirtualizedListProps<TItem>) => {
	const [scrollTop, setScrollTop] = useState(0);

	const totalHeight = items.length * rowHeight;
	const viewportHeight = Math.min(totalHeight, maxHeight);
	const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
	const visibleCount = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
	const endIndex = Math.min(items.length, startIndex + visibleCount);

	const visibleItems = useMemo(
		() =>
			items.slice(startIndex, endIndex).map((item, index) => {
				const absoluteIndex = startIndex + index;
				return { item, absoluteIndex };
			}),
		[items, startIndex, endIndex]
	);

	const handleScroll = (event: UIEvent<HTMLDivElement>) => {
		setScrollTop(event.currentTarget.scrollTop);
	};

	return (
		<Box sx={{ height: viewportHeight, overflowY: 'auto', width: '100%' }} onScroll={handleScroll}>
			<Box sx={{ position: 'relative', height: totalHeight, width: '100%' }}>
				{visibleItems.map(({ item, absoluteIndex }) => (
					<Box
						key={getItemKey(item)}
						sx={{
							position: 'absolute',
							top: absoluteIndex * rowHeight,
							left: 0,
							right: 0,
							height: rowHeight,
							overflow: 'hidden',
						}}
					>
						{renderItem(item)}
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default VirtualizedList;
